"use client";

import { motion } from "framer-motion";
import React, { useState } from "react";
import { ArrowRight, Building2, Tag } from "lucide-react";
import { FaBuilding } from "react-icons/fa6";
import VacancyModal from "src/components/molecules/Career/VacancyModal";
import ApplyFormModal from "src/components/molecules/Career/VaccancyApplyModal";

const RecentOpeningsSection = ({ vacancyList = [] }) => {
  const [selectedVacancy, setSelectedVacancy] = useState(null);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const activeVacancies = vacancyList.filter((v) => v.status === "active").sort((a, b) => new Date(b.vacancyDate) - new Date(a.vacancyDate));
  const displayedVacancies = showAll ? activeVacancies : activeVacancies.slice(0, 5);

  if (activeVacancies.length === 0) return null;

  return (
    <>
      <section id="recent-openings" className="relative w-full  font-body py-10! md:py-12! !px-6 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(59,130,246,0.08),transparent_45%)] pointer-events-none" />

        <div className="relative w-full">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between !mb-12 sm:!mb-14 md:!mb-16 !px-3 sm:!px-4 md:!px-6 lg:-translate-x-0">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
              <div className="flex items-center gap-4 mb-4!">
                <h3 className="text-xl sm:text-2xl font-medium uppercase text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600">
                  We Are Hiring
                </h3>
                <span className="w-16 h-[2px] bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600 rounded-full"></span>
              </div>

              <h2 className="!mt-2 text-text-primary leading-[1.05] tracking-[-0.05em] lg:text-[40px] text-[30px]">Recent Opening</h2>

              <p className="!mt-3 text-text-secondary text-[15px] leading-[1.7] max-w-[280px]">
                {activeVacancies.length} open {activeVacancies.length === 1 ? "position" : "positions"} across departments.
                <br />
                Find your fit and grow with us.
              </p>
            </motion.div>

            {!showAll && activeVacancies.length > 5 && (
              <button
                onClick={() => setShowAll(true)}
                className="group flex items-center gap-3 rounded-xl bg-gradient-to-r from-[#2563eb] to-[#7c3aed] text-white font-semibold text-[15px] !px-7 !py-4 shadow-[0_10px_40px_rgba(37,99,235,0.25)] transition-all duration-300 hover:scale-[1.03] !mt-8 lg:!mt-0 w-fit"
              >
                View All Openings
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 !gap-2  !px-3 sm:!px-4 md:!px-6">
            {displayedVacancies.map((vacancy) => (
              <div
                key={vacancy.id}
                className=" group relative flex flex-col overflow-hidden rounded-md border border-border-primary dark:border-white/[0.08] backdrop-blur-xl  transition-all duration-500 hover:-translate-y-2 hover:border-cyan-400/30 hover:shadow-[0_25px_80px_rgba(34,211,238,0.12)] before:absolute before:inset-0 before:bg-[radial-gradient(circle_at_top_right,rgba(168,85,247,0.16),transparent_35%)]  before:opacity-0 before:transition-opacity  before:duration-500 hover:before:opacity-100 "
                style={{
                  background: "var(--card-background)",
                  boxShadow: "var(--card-shadow)",
                }}
              >
                <div className="absolute left-0 top-0 h-[2px] w-0 bg-gradient-to-r from-purple-500 to-blue-600  to-cyan-400 transition-all duration-700 group-hover:w-full" />

                <div className=" relative z-10 flex  h-full  flex-col !p-5 sm:!p-6 xl:!p-7 ">
                  <div className="flex items-start justify-between !gap-4"></div>

                  <div className="!mt-2 ">
                    <h3 className="text-text-primary  text-[1.2rem]  sm:text-[1.3rem] xl:text-[1.42rem]  font-semibold  leading-[1.25] tracking-[-0.04em]  transition-all duration-300 group-hover:text-cyan-600 dark:group-hover:text-cyan-100 ">
                      {vacancy.title}
                    </h3>
                  </div>

                  <div className=" flex flex-wrap items-center  !gap-x-4  !gap-y-1 !mt-2  border-b border-border-primary/20 dark:border-white/[0.06] !pb-5 text-[15px] text-[var(--c-ink-3)] ">
                    {vacancy.department && (
                      <span className="flex items-center !gap-2">
                        <Building2 className="text-cyan-600 dark:text-cyan-300 w-4 h-4" />
                        {vacancy.department}
                      </span>
                    )}

                    {vacancy.category && (
                      <span className="flex items-center !gap-2">
                        <Tag className="w-4 h-4 text-green-600 dark:text-green-400" />

                        {vacancy.category}
                      </span>
                    )}
                  </div>

                  <div className="flex-1 !mt-5">
                    <p className=" line-clamp-4 text-[14px] text-text-secondary ">{vacancy.description?.replace(/<[^>]*>/g, "").trim()}</p>
                  </div>

                  <button
                    onClick={() => setSelectedVacancy(vacancy)}
                    className=" group/details  relative  !mt-7  flex items-center  justify-between  overflow-hidden rounded-sm border border-border-primary bg-surface-secondary/40 dark:bg-white/[0.03]  !px-5  !py-2  text-left  transition-all duration-300  hover:border-cyan-400/30  hover:bg-cyan-400/[0.05]  cursor-pointer"
                  >
                    <span className="  text-[15px]  font-semibold  tracking-[-0.02em] text-text-primary ">View Details</span>
                    <div className="  flex !h-10 !w-10 items-center justify-center  rounded-full bg-gradient-to-r from-purple-500 to-blue-600  transition-transform  duration-300 group-hover/details:translate-x-1  ">
                      <ArrowRight size={18} className="text-white" />
                    </div>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {selectedVacancy && !showApplyModal && <VacancyModal vacancy={selectedVacancy} onClose={() => setSelectedVacancy(null)} onApplyClick={() => setShowApplyModal(true)} />}

      {showApplyModal && selectedVacancy && (
        <ApplyFormModal
          vacancy={selectedVacancy}
          onClose={() => {
            setShowApplyModal(false);
            setSelectedVacancy(null);
          }}
        />
      )}
    </>
  );
};

export default RecentOpeningsSection;
